import { testimonials, faqs } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

export default function About() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '8090影院',
    url: window.location.origin,
    description: '8090影院网聚合免费电影大全资源，高清电影在线观看国内外大片。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@8090cinema.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="关于我们 - 8090影院 | 品牌介绍与联系方式"
        description="8090影院品牌介绍、用户口碑与联系方式。8090影院网聚合免费电影大全资源，高清电影在线观看国内外大片，打造不缓冲影院体验。"
        keywords="8090影院,关于8090影院,免费电影网站,高清电影平台,8090影院联系方式"
        structuredData={[orgSchema, faqSchema]}
      />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Page Header */}
            <div className="py-10 md:py-16 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-100 font-heading mb-4">
                关于8090影院
              </h1>
              <p className="text-foreground-400 max-w-[600px] mx-auto leading-relaxed">
                我们致力于为全球华语观众打造一个纯净、高清、流畅的在线电影观看平台
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {[
                { value: '5000+', label: '电影资源' },
                { value: '1200万+', label: '月活用户' },
                { value: '99.9%', label: '服务在线率' },
                { value: '12', label: '电影分类' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-6 rounded-xl bg-background-100 border border-background-200/50"
                >
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Brand Story */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-xl font-bold text-foreground-100 font-heading">品牌故事</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/50">
                  <h3 className="text-lg font-semibold text-foreground-200 font-heading mb-4">我们的初心</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    8090影院创立于2020年，创始团队是一群热爱电影的年轻人。在互联网内容付费日益普遍的时代，我们始终相信好电影应该被更多人看到，高质量的观影体验不应该是少数人的特权。
                  </p>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    五年来，我们坚持免费、高清、无广告的理念，从最初的小规模电影分享站点，逐步发展成为拥有超过五千万注册用户的综合性在线电影平台。我们用心筛选每一部电影，确保每一位观众都能获得最佳的观影体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/50">
                  <h3 className="text-lg font-semibold text-foreground-200 font-heading mb-4">我们的使命</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                    聚合全球优质电影资源，为华语观众提供最便捷、最高清的在线观影服务。8090影院不仅是电影资源的聚合平台，更是一个电影文化交流的社区。
                  </p>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    我们与多家内容版权方建立了合作关系，确保片源的丰富性和更新速度。同时，我们不断优化技术架构，提升服务器性能，让每一位用户都能享受到不缓冲、无卡顿的流畅观影体验。
                  </p>
                </div>
              </div>
            </section>

            {/* Core Values */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-accent-500 rounded-full" />
                <h2 className="text-xl font-bold text-foreground-100 font-heading">核心优势</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '完全免费',
                    desc: '无需注册、无需付费，所有电影资源免费开放观看',
                  },
                  {
                    icon: 'ri-hd-line',
                    title: '高清画质',
                    desc: '支持1080P高清播放，自动匹配最佳清晰度',
                  },
                  {
                    icon: 'ri-close-circle-line',
                    title: '纯净无广告',
                    desc: '全程无广告干扰，专注电影本身的观影体验',
                  },
                  {
                    icon: 'ri-speed-up-line',
                    title: '流畅不缓冲',
                    desc: '全球CDN加速节点，无论身在何处都能流畅观影',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="text-center p-6 rounded-xl bg-background-100 border border-background-200/50"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                      <i className={`${item.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-200 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-secondary-500 rounded-full" />
                <h2 className="text-xl font-bold text-foreground-100 font-heading">用户口碑</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="bg-background-100 rounded-xl p-6 border border-background-200/50"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i
                            className={`text-xs ${
                              i < t.rating
                                ? 'ri-star-fill text-primary-500'
                                : 'ri-star-line text-foreground-600'
                            }`}
                          ></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-300 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-200">{t.name}</span>
                      <span className="text-xs text-foreground-500">{t.location} · {t.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-xl font-bold text-foreground-100 font-heading">常见问题</h2>
              </div>
              <div className="space-y-3 max-w-[900px]">
                {faqs.map((faq, i) => (
                  <details
                    key={i}
                    className="group bg-background-100 rounded-xl border border-background-200/50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h4 className="text-sm font-medium text-foreground-200 pr-4">{faq.question}</h4>
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 transition-transform duration-300 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-xl font-bold text-foreground-100 font-heading">联系我们</h2>
              </div>
              <div className="bg-background-100 rounded-xl p-8 border border-background-200/50 max-w-[600px]">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-200 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-400">contact@8090cinema.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-customer-service-2-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-200 mb-1">在线客服</h4>
                      <p className="text-sm text-foreground-400">工作日 9:00 - 18:00（北京时间）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-200 mb-1">公司地址</h4>
                      <p className="text-sm text-foreground-400">中国北京市朝阳区光华路8090号电影大厦</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}